/**
 * useBookings - React Query hooks for booking data management
 * Provides caching, background refetching, and optimistic updates
 *
 * @module useBookings
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { bookingService } from '../services/bookingService';

// Stable empty array reference to prevent infinite re-renders in consuming components.
// Without this, `result.data?.bookings || []` creates a new [] on every render during loading,
// causing useEffect dependency loops in BookingsList.
const EMPTY_BOOKINGS = [];

/**
 * Fetch bookings with React Query caching
 * Automatically caches results and handles background refetching
 *
 * @param {Object} filters - Optional filters { propertyId, status, financial }
 * @param {Object} options - React Query options (optional)
 * @returns {Object} { data, bookings, isLoading, error, refetch }
 *
 * @example
 * // Get all bookings
 * const { bookings, isLoading } = useBookings();
 *
 * @example
 * // Get bookings for specific property
 * const { bookings } = useBookings({ propertyId: '123' });
 *
 * @example
 * // Get upcoming bookings
 * const { bookings } = useBookings({ status: 'upcoming' });
 */
export const useBookings = (filters = {}, options = {}) => {
  const result = useQuery({
    queryKey: ['bookings', filters],
    queryFn: () => bookingService.getAll(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    cacheTime: 10 * 60 * 1000, // 10 minutes - cached data kept
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnReconnect: true, // Refetch when internet reconnects
    retry: 1, // Retry once on failure
    ...options
  });

  return {
    ...result,
    bookings: result.data?.bookings ?? EMPTY_BOOKINGS,
    count: result.data?.count || 0
  };
};

/**
 * The /bookings list, one server page at a time with «عرض المزيد» (load more).
 * Every filter runs on the server, so `total` and `stats` describe the whole
 * filtered set, not the rows loaded so far (tasks/owner-screens-mobile.md D3).
 * Keeps the previous result on screen while a changed filter loads.
 *
 * @param {Object} filters - propertyId, ownerId, status, platform, search, checkInDayFrom/To
 * @param {number} pageSize
 * @returns {Object} { bookings, total, stats, fetchNextPage, hasNextPage, isFetchingNextPage, ... }
 */
export const useBookingsPaged = (filters = {}, pageSize = 20) => {
  const result = useInfiniteQuery({
    queryKey: ['bookings', 'paged', filters, pageSize],
    queryFn: ({ pageParam }) => bookingService.getPage({ ...filters, page: pageParam, limit: pageSize }),
    initialPageParam: 1,
    // Read defensively: the front end can deploy before the backend that adds hasMore.
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.page + 1 : undefined),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1
  });

  const pages = result.data?.pages;
  return {
    ...result,
    bookings: pages ? pages.flatMap(p => p?.bookings ?? []) : EMPTY_BOOKINGS,
    total: pages?.[0]?.total ?? 0,
    stats: pages?.[0]?.stats ?? null
  };
};

/**
 * Fetch single booking by ID
 *
 * @param {string} bookingId - Booking ID to fetch
 * @param {Object} options - React Query options (optional)
 * @returns {Object} { data, booking, isLoading, error }
 *
 * @example
 * const { booking, isLoading } = useBooking('booking_id_123');
 */
export const useBooking = (bookingId, options = {}) => {
  const result = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => bookingService.getById(bookingId),
    enabled: !!bookingId, // Only fetch if bookingId provided
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });

  return {
    ...result,
    booking: result.data?.booking || null
  };
};

/**
 * Cancel booking mutation
 * Automatically invalidates booking caches after cancellation
 *
 * @returns {Object} { cancelBooking, isLoading, error }
 *
 * @example
 * const { cancelBooking, isLoading } = useCancelBooking();
 *
 * const handleCancel = async () => {
 *   try {
 *     await cancelBooking('booking_id_123');
 *     alert('Booking cancelled!');
 *   } catch (error) {
 *     alert('Failed to cancel');
 *   }
 * };
 */
export const useCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bookingService.cancel,
    onSuccess: (data, bookingId) => {
      // Invalidate all booking queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });

    },
    onError: (error) => {
      console.error('❌ Failed to cancel booking:', error);
    }
  });
};

/**
 * Create booking mutation
 * Automatically invalidates booking caches after creation
 *
 * @returns {Object} { createBooking, isLoading, error }
 *
 * @example
 * const { createBooking, isLoading } = useCreateBooking();
 *
 * const handleCreate = async () => {
 *   try {
 *     await createBooking({
 *       propertyId: 'prop_123',
 *       bookingData: { ... }
 *     });
 *   } catch (error) {
 *     console.error(error);
 *   }
 * };
 */
export const useCreateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ propertyId, bookingData }) =>
      bookingService.create(propertyId, bookingData),
    onSuccess: () => {
      // Invalidate all booking queries
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (error) => {
      console.error('❌ Failed to create booking:', error);
    }
  });
};

/**
 * Update booking mutation
 * Automatically invalidates booking caches after update
 *
 * @returns {Object} { updateBooking, isLoading, error }
 *
 * @example
 * const { updateBooking, isLoading } = useUpdateBooking();
 *
 * const handleUpdate = async () => {
 *   try {
 *     await updateBooking({
 *       propertyId: 'prop_123',
 *       bookingId: 'booking_123',
 *       bookingData: { guestName: 'New Name' }
 *     });
 *   } catch (error) {
 *     console.error(error);
 *   }
 * };
 */
export const useUpdateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ propertyId, bookingId, bookingData }) =>
      bookingService.update(propertyId, bookingId, bookingData),
    onSuccess: (data, variables) => {
      // Invalidate all booking queries
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({
        queryKey: ['booking', variables.bookingId]
      });
    },
    onError: (error) => {
      console.error('❌ Failed to update booking:', error);
    }
  });
};

export default useBookings;
